import { makeAutoObservable } from 'mobx'
import type { IActionsProps, IIconsVector } from 'shared/ui'
import type { IResponseTrigger, ITriggerStatus, ITriggerType } from 'entities/Trigger/api/type'
import type { IResponseMediaShort } from 'entities/Attachment'
import { TriggerStatistic } from 'entities/Trigger/model/TriggerStatistic'
import { type Integration } from 'entities/Integrations/model/Integration'
import { Webhook } from 'entities/Webhook'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { Tag } from 'entities/Tags/model/Tag'
import { User } from 'entities/Users/model/User'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { Phone } from 'entities/Phone/model/Phone'
import { getSendFromInfo, type ISendFromInfo } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export type ITrigger = Trigger
export type ITriggerProps = {
  item: IResponseTrigger
  entities: IEntitiesStore
}

export class Trigger {
  id: number
  type: ITriggerType
  name: string
  ownerId: number | null
  owner: User | null
  status: ITriggerStatus
  private readonly _statistics: TriggerStatistic | null = null
  createdAt: string
  integration: Integration | null = null
  webhook: Webhook | null = null
  message = ''
  media_url: IResponseMediaShort[] = []
  is_track_conversion?: boolean
  conversion_id?: number | null = null
  sendFromNumbersProps: ISendFromInfo | null = null
  attachments: Attachment[] = []
  short_urls: IResponseTrigger['short_urls'] | null = []
  tags: Tag[]
  sendFrom: IResponseTrigger['send_from'] | null = null
  number_id: IResponseTrigger['number_id'] | null = null
  actionsProps: IActionsProps | null = null
  testNumberId = 0

  constructor(item: IResponseTrigger, private _entities: IEntitiesStore) {
    this.id = item.id
    this.type = item.is_rvm
      ? 'ringless'
      : item.send_from?.type === 'from_rcs_sender'
      ? 'rcs'
      : 'sms'
    this.name = item.name
    this.ownerId = item.owner_id
    this.owner = item.owner ? new User(item.owner) : null
    this.status = item.is_invalid ? 'invalid' : item.is_active ? 'active' : 'disabled'
    this._statistics = new TriggerStatistic(item, this._entities)
    this.createdAt = item.created_at
    this.message = item.message || ''
    this.media_url = item.media_url || []
    this.sendFromNumbersProps = this.getSendFromNumbersProps(item)
    this.testNumberId = this.getTestNumberId(item)
    this.integration = item.integration
      ? this._entities.integrationsStore.getIntegration(item.integration.key)
      : null
    this.attachments = Array.isArray(item.media_url)
      ? item.media_url.map((media) => new Attachment({ responseMediaShort: media }))
      : []
    this.short_urls = item.short_urls
    this.is_track_conversion = item.is_track_conversion
    this.conversion_id = item.conversion_id
    this.tags = Array.isArray(item.tags) ? item.tags.map((tagResponse) => new Tag(tagResponse)) : []
    this.sendFrom = item.send_from
    this.number_id = item.number_id

    if (item.webhook) {
      this.webhook = new Webhook(item.webhook)
    }

    makeAutoObservable(this)
  }

  setActionsProps = (actionProps: IActionsProps) => {
    this.actionsProps = actionProps
  }

  syncOrigin(updatedTrigger: IResponseTrigger) {
    this.status = updatedTrigger.is_invalid
      ? 'invalid'
      : updatedTrigger.is_active
      ? 'active'
      : 'disabled'
  }

  getSendIcon = (trigger: IResponseTrigger): IIconsVector | undefined => {
    const number = trigger.number
    if (number?.is_aircall) {
      return 'aircall'
    }
    if (trigger.send_from?.type === 'from_smart_inbox' && trigger.team) {
      return 'folder'
    }
    return 'inboxes2'
  }

  getTestNumberId = (trigger: IResponseTrigger): number => {
    if (trigger.send_from?.type === 'from_integration') trigger.integration_id as number
    if (trigger.team) return trigger.team.numbers[0]?.id ?? 0
    return trigger.number?.id ?? 0
  }

  getSendFromNumbersProps = (trigger: IResponseTrigger): ISendFromInfo | null => {
    return getSendFromInfo(trigger.send_from, {
      integrationKey: this.integration?.key,
      number: trigger.number ? new Phone(trigger.number, this._entities) : undefined,
      team: trigger.team ? new Inbox(trigger.team, this._entities) : undefined,
    })
  }

  get statistics() {
    return this._statistics
  }

  get inboxId() {
    if (this.sendFrom?.options.team_id) {
      const inbox = this._entities.inboxesStore.getItem(this.sendFrom?.options.team_id)

      return inbox?.id
    }

    if (this.sendFrom?.options.number_id) {
      const inbox = this._entities.inboxesStore.getInboxByNumber(this.sendFrom?.options.number_id)

      return inbox?.id
    }

    if (this.number_id) {
      const inbox = this._entities.inboxesStore.getInboxByNumber(this.number_id)

      return inbox?.id
    }
  }

  get isInvalid() {
    return this.status === 'invalid'
  }
}
